<template>
    <div>
        <vx-selectable
            :multiple="true"
            :no-tags="true"
            @search="search"
            placeholder="Select External List"
            :options="options"
            v-model="externalContactLists"
            :clearable="true"
            :dropdownShouldOpen="dropdownShouldOpen"
            :deselectFromDropdown="true"
            :relativeDropdown="true"
            :closeOnSelect="false"
            :clearSearchOnSelect="false"
            @update:modelValue="emit('update:modelValue', $event)"
            :disabled="props.disabled"
            :size="props.size"
            :error="props.error"
        >
            <template v-slot:option="{ option }">
                <external-contact-lists-selectable-row
                    :externalContactList="option"
                    :contactsCount="contactsCount[option.id]"
                    :selected="isSelected(option)"
                >
                </external-contact-lists-selectable-row>
            </template>
        </vx-selectable>

        <div
            v-if="externalContactLists.length"
            class="border border-solid border-slate-300 rounded-lg mt-6"
        >
            <div
                data-test="to-external-contact-lists-selected"
                v-for="externalContactList in externalContactLists"
                :key="externalContactList.id"
                tabindex="0"
                role="button"
                class="appearance-none border-x-0 border-t-0 last:border-b-0 border-b border-slate-300 border-solid  flex items-center justify-between px-4 text-slate-700 text-sm"
                @click="remove(externalContactList)"
                @keydown.enter="remove(externalContactList)"
                @keydown.space="remove(externalContactList)"
            >
                <external-contact-lists-selectable-row
                    :disabled="props.disabled"
                    :externalContactList="externalContactList"
                    :contactsCount="contactsCount[externalContactList.id]"
                    :selected="isSelected(externalContactList)"
                    class="py-2"
                >
                </external-contact-lists-selectable-row>
            </div>
        </div>
    </div>
</template>

<script setup>
import { VxSelectable } from '@voxie/frontend-components';
import { externalListsClient } from '~/services';
import { ref, onMounted, watch, computed } from 'vue';
import debounce from '~/utils/debounce';
import {
    AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL, AUDIENCE_TYPE_ACQUISITION, AUDIENCE_TYPE_RE_ACQUISITION
} from '~/constants/campaign';
import ExternalContactListsSelectableRow
    from "~/components/general/external-contact-lists/ExternalContactListsSelectableRow.vue";
import {useExternalListsContactCount} from '~/composables/useExternalListsContactCount';
import { useCurrentGroup } from '~/composables/useCurrentGroup';

const teamId = Spark.state.currentTeam.id;
const { currentGroup, currentOrgUnit } = useCurrentGroup();
const context = computed(() => ({ teamId, orgUnitId: currentOrgUnit.value?.id, groupId: currentGroup.value?.group_id }));

const props = defineProps({
    modelValue: {
        type: Array,
        required: false,
        default: () => [],
    },
    audienceType: {
        type: String,
        default: undefined,
        validator: (audienceType) => [AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL, AUDIENCE_TYPE_ACQUISITION, AUDIENCE_TYPE_RE_ACQUISITION].includes(audienceType)
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String,
        default: 'md'
    },
    flowId: {
        type: [String, undefined],
        default: undefined,
    },
    error: String,
    groupIds: {
        type: Array,
        required: false,
    },
});

const emit = defineEmits(["update:modelValue"]);

watch(
    () => props.modelValue,
    (newModelValue) => {
        externalContactLists.value = newModelValue;
    }
);

watch(
    () => props.audienceType,
    () => {
        search();
    }
);

watch(
    () => props.groupIds,
    () => {
        search();
    }
);

const externalContactLists = ref(props.modelValue.map((externalContactList) => {
    return {
        ...externalContactList,
        // Fill in the missing label property for the selected external contact lists
        label: `${externalContactList.id} - ${externalContactList.name}`,
    }
}));
const { contactsCount, fetchExternalListsContactCount } = useExternalListsContactCount();

const options = ref([]);

const isSelected = (externalContactList) => {
    return Boolean(externalContactLists.value.find((item) => item.id === externalContactList.id));
};

const remove = async (externalContactList) => {
    if (props.disabled) {
        return;
    }

    externalContactLists.value = externalContactLists.value.filter((item) => item.id !== externalContactList.id);
    emit("update:modelValue", externalContactLists.value);
};

const search = debounce(async (search, loading) => {
    loading?.(true);

    const params = {
        per_page: 10,
        query: search,
        filter: {},
    };

    try {
        const response = await externalListsClient.getExternalLists(context.value, params);
        options.value = response.data.data.map((externalContactList) => {
            return {
                ...externalContactList,
                label: `${externalContactList.id} - ${externalContactList.name}`,
            }
        });

        fetchExternalListsContactCount(context.value, {
            external_contact_list_ids: options.value.map(externalContactList => externalContactList.id),
            group_ids: props.groupIds,
            audience_type: props.audienceType,
        });
    } finally {
        loading?.(false);
    }
}, 300);

const dropdownShouldOpen = (VueSelect) => {
    return VueSelect.search.length > 0 || VueSelect.open;
};

onMounted(() => {
    search();

    // Fetch the contact count for the preselected external contact lists
    // (these might not be in the 10 options fetched by the search function)
    fetchExternalListsContactCount(context.value, {
        external_contact_list_ids: externalContactLists.value.map(externalContactList => externalContactList.id),
        group_ids: props.groupIds,
        audience_type: props.audienceType,
    });
});
</script>
